import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { AdminListPage } from '../components/AdminListPage'
import { useAdminSeasons } from '../hooks/useAdminSeasons'
import type { SeasonDocument } from '@/types'

export function AdminSeasonsPage() {
  const navigate = useNavigate()
  const { seasons, loading, error, deleteSeason } = useAdminSeasons()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<(SeasonDocument & { id: string }) | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  async function handleDelete() {
    if (!deleteTarget) return
    setDeletingId(deleteTarget.id)
    try {
      await deleteSeason(deleteTarget.id)
    } finally {
      setDeletingId(null)
      setDeleteTarget(null)
      setDialogOpen(false)
    }
  }

  function openDeleteDialog(season: SeasonDocument & { id: string }) {
    setDeleteTarget(season)
    setDialogOpen(true)
  }

  return (
    <AdminListPage
      title="Seasons"
      action={
        <Button asChild>
          <Link to="/admin/seasons/new">Add Season</Link>
        </Button>
      }
      error={error}
      loading={loading}
      skeletonRows={4}
      dialog={
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger className="hidden" />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Season</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-foreground">{deleteTarget?.title}</span>? This
              action cannot be undone.
            </p>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDialogOpen(false)
                  setDeleteTarget(null)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleDelete()}
                disabled={deletingId !== null}
              >
                {deletingId !== null ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Season #</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Year</TableHead>
            <TableHead>Published</TableHead>
            <TableHead>Order</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <MultiButtonGroup>
          <TableBody>
            {seasons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No seasons yet. Add one to get started.
                </TableCell>
              </TableRow>
            ) : (
              seasons.map((season) => (
                <TableRow key={season.id}>
                  <TableCell className="font-medium">{season.number}</TableCell>
                  <TableCell>{season.title}</TableCell>
                  <TableCell>{season.year}</TableCell>
                  <TableCell>
                    <Badge variant={season.isPublished ? 'default' : 'secondary'}>
                      {season.isPublished ? 'Published' : 'Draft'}
                    </Badge>
                  </TableCell>
                  <TableCell>{season.order}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      <MultiButton
                        variant="outline"
                        size="sm"
                        items={[
                          { id: 'edit', icon: Pencil, label: 'Edit', onClick: () => navigate(`/admin/seasons/${season.id}/edit`) },
                          { id: 'delete', icon: Trash2, label: 'Delete', onClick: () => openDeleteDialog(season), disabled: deletingId === season.id, className: 'text-destructive hover:text-destructive' },
                        ]}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </MultiButtonGroup>
      </Table>
    </AdminListPage>
  )
}
